import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Overline, Cta } from "@/components/site/ui";
import { LeadForm } from "@/components/site/LeadForm";
import { Minus, Plus, Download } from "lucide-react";
import { IMAGES } from "@/data/content";
import { jsPDF } from "jspdf";

const TIERS = [
  { key: "Essential", mult: 1, blurb: "Smart, durable finishes" },
  { key: "Premium", mult: 1.9, blurb: "Designer materials & hardware" },
  { key: "Luxury", mult: 3.2, blurb: "Bespoke, top-of-the-line" },
];

const ROOMS = [
  { key: "kitchen", label: "Modular Kitchen", base: 150000, max: 3 },
  { key: "wardrobe", label: "Wardrobes", base: 55000, max: 8 },
  { key: "living", label: "Living / Dining", base: 120000, max: 3 },
  { key: "bedroom", label: "Bedrooms", base: 95000, max: 6 },
];

const inr = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN");

const Stepper = ({ value, onChange, max, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <button
      type="button"
      onClick={() => onChange(Math.max(0, value - 1))}
      className="h-9 w-9 rounded-full border border-black/15 flex items-center justify-center hover:border-[#1C1A17] hover:text-[#1C1A17] transition-colors"
      aria-label="decrease"
    >
      <Minus className="h-4 w-4" />
    </button>
    <span className="w-8 text-center font-head text-xl font-semibold">{value}</span>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + 1))}
      className="h-9 w-9 rounded-full border border-black/15 flex items-center justify-center hover:border-[#1C1A17] hover:text-[#1C1A17] transition-colors"
      aria-label="increase"
    >
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

export default function InstantQuote() {
  const [tier, setTier] = useState("Premium");
  const [counts, setCounts] = useState({ kitchen: 1, wardrobe: 2, living: 1, bedroom: 2 });

  const mult = TIERS.find((t) => t.key === tier).mult;

  const subtotal = useMemo(
    () => ROOMS.reduce((sum, r) => sum + counts[r.key] * r.base * mult, 0),
    [counts, mult]
  );
  const low = subtotal * 0.9;
  const high = subtotal * 1.12;

  const summary = useMemo(() => {
    const lines = ROOMS.filter((r) => counts[r.key] > 0).map(
      (r) => `${counts[r.key]}× ${r.label}`
    );
    return `Instant Quote (${tier} tier): ${lines.join(", ")}. Estimated range ${inr(low)}–${inr(high)}.`;
  }, [counts, tier, low, high]);

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    // Header band
    doc.setFillColor(15, 15, 15);
    doc.rect(0, 0, W, 90, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("SPHERIO LIVING", 40, 45);
    doc.setTextColor(184, 147, 90);
    doc.text(".", 210, 45);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    doc.text("Designing the Future of Living", 40, 62);
    doc.setTextColor(200, 200, 200);
    doc.text(new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }), W - 40, 45, { align: "right" });
    doc.text("Instant Quote", W - 40, 62, { align: "right" });

    // Title
    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("Your indicative estimate", 40, 140);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(75, 85, 99);
    doc.text(`Finish level: ${tier}`, 40, 162);

    // Table header
    let y = 210;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(184, 147, 90);
    doc.text("ROOM", 40, y);
    doc.text("QTY", 300, y);
    doc.text("PER UNIT", 380, y, { align: "right" });
    doc.text("SUBTOTAL", W - 40, y, { align: "right" });
    doc.setDrawColor(230, 230, 230);
    doc.line(40, y + 6, W - 40, y + 6);

    // Rows
    doc.setFont("helvetica", "normal");
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(11);
    y += 26;
    let total = 0;
    ROOMS.filter((r) => counts[r.key] > 0).forEach((r) => {
      const per = r.base * mult;
      const sub = per * counts[r.key];
      total += sub;
      doc.text(r.label, 40, y);
      doc.text(String(counts[r.key]), 300, y);
      doc.text("Rs. " + Math.round(per).toLocaleString("en-IN"), 380, y, { align: "right" });
      doc.text("Rs. " + Math.round(sub).toLocaleString("en-IN"), W - 40, y, { align: "right" });
      y += 22;
    });

    // Total band
    y += 12;
    doc.setDrawColor(184, 147, 90);
    doc.setLineWidth(1);
    doc.line(40, y, W - 40, y);
    y += 26;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Estimated range", 40, y);
    doc.setFontSize(18);
    doc.setTextColor(184, 147, 90);
    doc.text(
      "Rs. " + Math.round(low).toLocaleString("en-IN") + "  -  Rs. " + Math.round(high).toLocaleString("en-IN"),
      W - 40, y, { align: "right" }
    );

    // Notes
    y += 40;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    const notes = [
      "This is an indicative estimate. Your free consultation gives you an exact, itemised quote.",
      "Includes design, materials and installation for the selected rooms. Civil, plumbing and electrical work quoted separately.",
      "Every Spherio Living project carries a 10-year warranty on modular cabinetry.",
    ];
    notes.forEach((n) => { doc.text("• " + n, 40, y, { maxWidth: W - 80 }); y += 18; });

    // Footer
    doc.setDrawColor(230, 230, 230);
    doc.line(40, 780, W - 40, 780);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text("Spherio Living  •  SCO No. 222, Shopping Square 1, Sector D, Sushant Golf City, Lucknow 226030  •  hello@spherioliving.com  •  +91 7388047001", 40, 800);

    doc.save(`Spherio-Living-Quote-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  return (
    <>
      <section className="relative bg-[#1C1A17] text-white pt-36 pb-16 overflow-hidden">
        <img src={IMAGES.kitchen1} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-[#1C1A17]/70" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10">
          <Overline dark>Get Instant Quote</Overline>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mt-5 max-w-3xl">
            Build your estimate, room by room.
          </h1>
          <p className="mt-5 max-w-xl text-white/65 text-lg">
            Pick your rooms and finish level for a live ballpark. It’s indicative — your free
            consultation gives you an exact, itemised quote.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Calculator */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#1C1A17]">1 — Finish level</div>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`tier-${t.key}`}
                  onClick={() => setTier(t.key)}
                  className={`rounded-2xl border p-5 text-left transition-colors duration-300 ${
                    tier === t.key ? "border-[#1C1A17] bg-[#1C1A17] text-white" : "border-black/15 hover:border-[#1C1A17]/50"
                  }`}
                >
                  <div className="font-head text-lg font-semibold">{t.key}</div>
                  <div className={`text-sm mt-1 ${tier === t.key ? "text-white/70" : "text-[#5C574F]"}`}>{t.blurb}</div>
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 text-[11px] tracking-[0.28em] uppercase font-bold text-[#1C1A17]">2 — Your rooms</div>
            <div className="mt-4 divide-y divide-black/10 border border-black/10 rounded-2xl overflow-hidden">
              {ROOMS.map((r) => (
                <div key={r.key} className="flex items-center justify-between p-6 bg-white">
                  <div>
                    <div className="font-medium">{r.label}</div>
                    <div className="text-sm text-[#5C574F]">from {inr(r.base * mult)} each</div>
                  </div>
                  <Stepper
                    testid={`room-${r.key}`}
                    value={counts[r.key]}
                    max={r.max}
                    onChange={(v) => setCounts((c) => ({ ...c, [r.key]: v }))}
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Estimate + lead form */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 space-y-6">
            <motion.div layout className="rounded-3xl bg-[#1C1A17] text-white p-8">
              <div className="text-white/50 text-sm">Estimated project cost</div>
              <div className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-2" data-testid="estimate-range">
                {inr(low)} <span className="text-white/40">–</span> {inr(high)}
              </div>
              <div className="mt-3 text-sm text-white/50">{tier} finish · indicative range</div>
              <div className="mt-6 h-px bg-white/10" />
              <p className="mt-4 text-sm text-white/60">
                Share your details below and we’ll send a precise, itemised quote with a free 3D design.
              </p>
              <button
                type="button"
                data-testid="download-quote-pdf"
                onClick={downloadPdf}
                className="mt-6 group inline-flex items-center gap-2 rounded-full bg-white text-[#0A0A0A] px-5 py-2.5 text-sm font-semibold hover:bg-[#1C1A17] hover:text-white transition-colors"
              >
                <Download className="h-4 w-4" /> Download quote as PDF
              </button>
            </motion.div>

            <div className="rounded-3xl border border-black/10 p-6 lg:p-8">
              <h3 className="font-head text-xl font-medium">Get my exact quote</h3>
              <div className="mt-6">
                <LeadForm source="quote" ctaLabel="Send My Quote Request" defaults={{ service: "Full Home Interior", message: summary }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <div className="rounded-2xl bg-[#F5F2ED] border border-black/5 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-[#5C574F] max-w-xl">Prefer to talk it through first? Book a free consultation and we’ll estimate together.</p>
          <Cta to="/book-consultation" data-testid="quote-book-cta">Book Free Consultation</Cta>
        </div>
      </section>
    </>
  );
}
